import { Box, Chip, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { getEndCustomerAccountReminders } from "API/swesum";
import {
  DataGrid,
  getGridStringOperators,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import { getSystemStatusLabelById } from "utils/labels";
import { getSystemStatusColorById } from "utils/colors";

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{
        paddingRight: "2rem",
        justifyContent: "flex-end",
      }}
    >
      <GridToolbarFilterButton />
      <GridToolbarExport
        csvOptions={{
          fileName: "påminnelser",
          utf8WithBom: true,
          delimiter: ";",
          lineEnd: "\r\n",
        }}
        printOptions={{ disableToolbarButton: true }}
      />
    </GridToolbarContainer>
  );
}

const onlyContains = getGridStringOperators().filter(
  (op) => op.value === "contains"
);

const onlyEquals = getGridStringOperators().filter(
  (op) => op.value === "equals"
);

export default function RemindersTable() {
  const { openError } = usePopupNotificationContext();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const [sortModel, setSortModel] = useState([
    { field: "BookingDate", sort: "desc" },
  ]);

  const [filterModel, setFilterModel] = useState({
    items: [{ field: "SystemStatus", value: "1", operator: "equals" }],
    quickFilterValues: [],
  });

  const [rowCount, setRowCount] = useState(0);

  const debouncedSetFilterModel = useCallback(
    debounce((newFilterModel) => {
      setFilterModel(newFilterModel);
    }, 500),
    []
  );

  function debounce(func, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getEndCustomerAccountReminders({
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          column:
            filterModel.items.length > 0 ? filterModel.items[0].field : null,
          value:
            filterModel.items.length > 0 ? filterModel.items[0].value : null,
          operation:
            filterModel.items.length > 0 ? filterModel.items[0].operator : null,
          sortField: sortModel[0]?.field,
          sortOrder: sortModel[0]?.sort,
        });

        if (response?.data) {
          setRows(response.data.Rows);
          setRowCount(response.data.Count);
        } else {
          openError(`Något gick fel: ${response}`);
        }
      } catch (error) {
        openError(
          `Kunde inte hämta påminnelser: ${
            error.response ? error.response.data : error
          }`
        );
      }
    };

    fetchData();
  }, [paginationModel, sortModel, filterModel]);

  return (
    <Grid sx={{ padding: 3 }} item xs={12} md={12}>
      <Typography sx={{ marginBottom: "1.0rem" }} variant="h5">
        Påminnelser
      </Typography>

      <DataGrid
        columns={[
          {
            field: "BookingDate",
            headerName: "Bokföringsdatum",
            width: 200,
            filterable: false,
            cellClassName: "first-cell",
          },
          {
            field: "EndCustomer.Name",
            headerName: "Kundnamn",
            valueGetter: (_, row) => row.EndCustomer.Name,
            minWidth: 350,
            flex: 1,
            filterOperators: onlyContains,
            renderCell: (params) => {
              return (
                <Box
                  sx={{
                    width: "100%",
                    textDecoration: "underline",
                    color: "blue",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/endcustomers/${params.row.EndCustomer.Id}`);
                  }}
                >
                  {params.value}
                </Box>
              );
            },
          },
          {
            field: "EndCustomer.ExternalCustomerNr",
            headerName: "Kundnummer",
            valueGetter: (_, row) => row.EndCustomer.ExternalCustomerNr,
            width: 125,
            filterable: false,
          },
          {
            field: "Client.Name",
            headerName: "Klientnamn",
            valueGetter: (_, row) => row.Client.Name,
            minWidth: 350,
            flex: 1,
            filterOperators: onlyContains,
            renderCell: (params) => {
              return (
                <Box
                  sx={{
                    width: "100%",
                    textDecoration: "underline",
                    color: "blue",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/clients/${params.row.Client.Id}`);
                  }}
                >
                  {params.value}
                </Box>
              );
            },
          },
          {
            field: "Client.CNr",
            headerName: "Klientnummer",
            valueGetter: (_, row) => row.Client.CNr,
            width: 125,
            filterable: false,
          },
          {
            field: "Description",
            headerName: "Beskrivning",
            width: 200,
            filterable: false,
          },
          {
            field: "InvoiceReference",
            headerName: "Fakturanummer",
            width: 125,
            filterable: false,
          },
          {
            field: "OCR",
            headerName: "OCR",
            width: 150,
            filterable: false,
          },
          {
            field: "Amount",
            headerName: "Ursprungligt (kr)",
            width: 150,
            filterable: false,
          },
          {
            field: "InvoiceView.ReminderRemainingAmount",
            headerName: "Återstående (kr)",
            valueGetter: (_, row) => row.ReminderRemainingAmount,
            width: 150,
            filterable: false,
          },
          {
            field: "SystemStatus",
            headerName: "Status",
            width: 135,
            filterOperators: onlyEquals,
            renderCell: (params) => {
              return (
                <Chip
                  label={getSystemStatusLabelById(params.value)}
                  color={getSystemStatusColorById(params.value)}
                  sx={{
                    height: 24,
                    width: 80,
                    "&:hover": { opacity: 0.8, cursor: "pointer" },
                  }}
                />
              );
            },
          },
        ]}
        rows={rows}
        getRowId={(e) => e.Id}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50, 100]}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={debouncedSetFilterModel}
        rowCount={rowCount}
        onRowClick={(params) =>
          navigate(`/endcustomersaccounts/${params.row.Id}`)
        }
        disableRowSelectionOnClick
        density="compact"
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          panel: { placement: "bottom-end" }, // by default its bottom-start
        }}
        localeText={{
          toolbarExport: "Ladda ner",
          toolbarExportCSV: "Ladda ner CSV",
          toolbarFilters: "Filter",
        }}
        sx={{
          paddingTop: "1rem",
          cursor: "pointer",
          backgroundColor: "#fff",
          boxShadow:
            "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
          ".MuiTable-root": {
            ".MuiTableCell-root": {
              "&:nth-of-type(1)": {
                paddingTop: "0",
                paddingBottom: "0",
              },
            },
          },
          "& .even-row": { backgroundColor: "#fafafa" },
          "& .odd-row": { backgroundColor: "#fff" },
          "& .first-cell": {
            paddingLeft: 4,
          },
        }}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
        }
      />
    </Grid>
  );
}
